<script setup lang="ts">
import { inject } from 'vue';
import { CatalogueBrowserMode } from '@/models/Catalogue';
import type { CatalogueItem, CatalogueItemGroup } from '@/models/Catalogue';
import { getAllCatalogueGroupsFlat } from '@/composables/catalogue/useCatalogueGroupHierarchy';
import CatalogueItemCard from '@/components/catalogue/CatalogueItemCard.vue';
import { CatalogueItemActionsKey } from '@/composables/catalogue/useCatalogueItemActions';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

const props = withDefaults(
  defineProps<{
    items: CatalogueItem[];
    groups: CatalogueItemGroup[];
    mode?: CatalogueBrowserMode;
    loading?: boolean;
    selectedItem?: CatalogueItem | null;
    selectable?: boolean;
  }>(),
  {
    mode: CatalogueBrowserMode.BROWSE,
    loading: false,
    selectedItem: null,
    selectable: false,
  },
);

const actions = inject(CatalogueItemActionsKey)!;

const emit = defineEmits<{
  (e: 'select', item: CatalogueItem): void;
}>();

// Helper function to get group name from UUID
const getGroupName = (groupUuid: string | undefined) => {
  if (!groupUuid) {
    return '—';
  }
  const flatGroups = getAllCatalogueGroupsFlat(props.groups);
  const group = flatGroups.find((g) => g.uuid === groupUuid);
  return group?.name ?? '—';
};

const handleItemClick = (item: CatalogueItem) => {
  if (props.mode === CatalogueBrowserMode.SELECT && props.selectable) {
    emit('select', item);
  } else {
    actions.viewItem(item);
  }
};

const isSelected = (item: CatalogueItem) => {
  return props.selectedItem?.uuid === item.uuid;
};

// Expose for template comparison
const isBrowseMode = () => props.mode === CatalogueBrowserMode.BROWSE;
</script>

<template>
  <!-- Loading state -->
  <div v-if="loading" class="d-flex justify-center py-8">
    <v-progress-circular indeterminate color="primary" size="48" />
  </div>

  <!-- Empty state -->
  <v-alert
    v-else-if="items.length === 0"
    type="info"
    variant="tonal"
    class="mt-4"
  >
    {{ $gettext('No items found') }}
  </v-alert>

  <!-- Browse mode: Card grid -->
  <v-row v-else-if="isBrowseMode()" dense>
    <v-col
      v-for="item in items"
      :key="item.uuid"
      cols="12"
      sm="6"
      md="4"
      lg="2"
      class="d-flex"
    >
      <CatalogueItemCard :item="item" :show-actions="true" />
    </v-col>
  </v-row>

  <!-- Select mode: List view -->
  <v-list v-else class="overflow-y-auto catalogue-select-list">
    <v-list-item
      v-for="item in items"
      :key="item.uuid"
      :active="isSelected(item)"
      @click="handleItemClick(item)"
    >
      <v-list-item-title>{{ item.name }}</v-list-item-title>
      <v-list-item-subtitle>
        {{ $gettext('Category') }}: {{ item.category || '—' }} |
        {{ $gettext('Group') }}: {{ getGroupName(item.groupUuid) }}
      </v-list-item-subtitle>
    </v-list-item>
  </v-list>
</template>

<style scoped>
.catalogue-select-list {
  max-height: 300px;
}

.v-col {
  display: flex;
}

.v-col > * {
  width: 100%;
}
</style>
